<template>
  <div>
    <v-dialog v-model="deleteDialog" max-width="300px">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          dark
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{ $t('Tem certeza de que deseja excluir esse item?') }}
          </div>

          <v-btn
            :disabled="deleteLoading"
            class="ma-1"
            color="grey"
            text
            @click="deleteDialog = false"
          >
            Cancelar
          </v-btn>

          <v-btn
            :loading="deleteLoading"
            class="ma-1"
            color="error"
            text
            @click="removeCalibration(serviceOrder)"
          >
            {{ $t('Excluir') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>

    <div class="hidden-sm-and-down">
      <div class="d-flex">
        <v-spacer></v-spacer>
        <v-tooltip bottom v-if="selected && selected.length > 0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              depressed
              class="mb-2 mr-2 primary-button"
              dark
              @click="getZip"
            >
              {{ $t('Baixar certificados') }}
            </v-btn>
          </template>
          <span>{{ $t('Máximo de 25 arquivos por vez') }}</span>
        </v-tooltip>
        <v-btn
          depressed
          class="mb-2 ml-2 primary-button"
          dark
          @click="visible = !visible"
        >
          {{ $t('Filtros') }}
        </v-btn>
      </div>
    </div>

    <v-expand-transition>
      <div v-show="visible">
        <v-row class="ma-0" dense>
          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Equipamento') }}</label>
            <v-text-field
              hide-details="auto"
              v-model="filter.equipment"
              outlined
              class="primary-input"
              @keyup.enter="filterTable"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Identificação') }}</label>
            <v-text-field
              hide-details="auto"
              v-model="filter.identification"
              outlined
              class="primary-input"
              @keyup.enter="filterTable"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Nº O.S.') }}</label>
            <v-text-field
              hide-details="auto"
              v-model="filter.number"
              outlined
              class="primary-input"
              @keyup.enter="filterTable"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-col cols="12" class="d-flex justify-end">
          <v-btn
            depressed
            class="mr-2 secondary-button"
            color="#BBCF20"
            @click="filterTable"
          >
            <span>Filtrar</span>
            <v-icon class="pl-2">mdi-magnify</v-icon>
          </v-btn>

          <v-btn
            depressed
            color="#20202066"
            class="clear-button"
            @click="reset"
          >
            <span>{{ $t('Limpar') }}</span>
            <v-icon class="pl-2">mdi-cached</v-icon>
          </v-btn>
        </v-col>
      </div>
    </v-expand-transition>

    <div class="wrapper desktop">
      <v-data-table
        :loading="loading"
        :loading-text="$t('Carregando...')"
        :no-data-text="$t('Nenhum item encontrado')"
        :headers="headers"
        :items="electricalSafetyItems"
        v-model="selected"
        show-select
        hide-default-footer
        :items-per-page="pageSize"
        :header-props="headerProps"
        class="main-table"
      >
        <template #[`item.identification`]="{ item }">
          {{
            item.equipment.identification
              ? item.equipment.identification
              : 'sem registro'
          }}
        </template>

        <template #[`item.equipment`]="{ item }">
          {{ item.equipment.name ? item.equipment.name : 'sem registro' }}
        </template>

        <template #[`item.number`]="{ item }">
          {{ item.number ? item.number : 'sem registro' }}
        </template>

        <template #[`item.calibrationDateDisplay`]="{ item }">
          {{ formatDate(item.calibrationDateDisplay) }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="ma-1"
                :color="item.closeServiceOrderDateTime ? '#B9CE1E' : '#F5F5F5'"
                dark
                @click.stop="openServiceOrder(item)"
                v-bind="attrs"
                v-on="on"
                small
                depressed
              >
                <v-icon color="#2A8EA4" small>mdi-view-module</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Ordem de serviço') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="item?.arkMedsChecklist"
                class="ma-1"
                color="#0F3565"
                dark
                small
                depressed
                @click.stop="openElectricalSafety(item)"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small> mdi-pencil </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Editar') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="institution.userType <= 1"
                v-show="item.serviceOrderType === 1"
                x-small
                class="ma-1"
                :color="'#BBCF20'"
                :disabled="!item?.arkMedsChecklist"
                small
                depressed
                @click.stop="generateElectricalSafetyChecklistPDF(item)"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi-printer</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Laudo segurança elétrica') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-show="
                  item.serviceOrderType === 1 &&
                  Array.isArray(item.uploadDocuments) &&
                  item.uploadDocuments.length
                "
                x-small
                class="ma-1"
                :color="'#0F3565'"
                :disabled="!item?.uploadDocuments"
                small
                depressed
                @click.stop="uploadDocument(item)"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi-open-in-new</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Laudo segurança elétrica') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <div class="pagination">
        <div class="item-pagination block-one">
          <v-menu bottom offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                class="btn"
                depressed
                color="#C0C0C0"
                small
              >
                qtd: {{ pageSize }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(size, i) in sizes"
                :key="i"
                @click="pageSize = size"
              >
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <div class="registros" v-if="totalItensOnPage">
            <span> 1 - {{ totalItensOnPage }} de {{ totalItems }} </span>
          </div>
        </div>

        <div class="item-pagination block-two">
          <v-pagination
            class="pagination-item"
            v-model="page"
            :length="totalPages"
            :total-visible="3"
          ></v-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { color } from 'echarts'
import ApiService from '../../../../services/ApiService'

export default {
  data: () => ({
    selected: [],
    loading: false,
    apiService: new ApiService(),
    deleteLoading: false,
    deleteDialog: false,
    visible: false,
    page: 1,
    pageSize: 10,
    totalPages: 1,
    sizes: [10, 25, 50],
    totalItems: 0,
    totalItensOnPage: 0,
    electricalSafetyItems: [],
    userLogged: {},
    userType: null,
    institutionId: null,
    currentServiceOrder: null,
    filter: {
      equipment: null,
      identification: null,
      number: null,
    },
    headers: [
      { text: '', align: 'center', value: '', sortable: false },
      {
        text: 'Identificação',
        align: 'center',
        value: 'equipment.identification',
        sortable: false,
      },
      {
        text: 'Equipamento',
        align: 'center',
        value: 'equipment.name',
        sortable: false,
      },
      {
        text: 'Nº OS',
        align: 'center',
        value: 'number',
        sortable: false,
      },
      {
        text: 'Data Certificado',
        align: 'center',
        value: 'calibrationDateDisplay',
        sortable: false,
      },
      {
        text: 'Ações',
        align: 'center',
        value: 'actions',
        sortable: false,
      },
    ],
    headerProps: {
      sortByText: 'Ordenar por',
    },
  }),

  created() {
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.institutionId = this.institution.id
    this.userLogged = JSON.parse(localStorage.getItem('user'))
    this.userType = this.institution.userType
    this.getOS()
  },

  methods: {
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    async removeCalibration(serviceOrder) {
      await this.apiService
        .delete(
          `ElectricalSafety/remove-checklist/${serviceOrder.arkMedsChecklist.id}`
        )
        .then((response) => {
          if (response.statusCode === 200) {
            this.$toast('Certificado removido')
          } else {
            this.$toast('Erro ao remover certificado')
          }
        })
    },

    openServiceOrder(item) {
      const route = this.$router.resolve({
        path: `/editServiceOrder/${item.id}`,
      })
      window.open(route.href, '_blank')
    },

    filterTable() {
      this.page = 1
      this.getOS()
    },

    async getOS() {
      this.filter.institutionId = this.institutionId
      this.filter.page = this.page
      this.filter.pageSize = this.pageSize

      await this.apiService
        .post(`ElectricalSafety/get-by-institutionId`, this.filter)
        .then((response) => {
          if (response.statusCode === 200) {
            this.electricalSafetyItems = response.content.data.map((os) => {
              return {
                ...os,
                calibrationDateDisplay:
                  os.arkMedsChecklist?.calibrationDate ||
                  os.closeServiceOrderDateTime ||
                  null,
              }
            })
            this.totalItems = response.content.pager.totalItems
            this.totalPages = response.content.pager.totalPages
            this.totalItensOnPage = response.content.data.length
          }
        })
    },

    async generateElectricalSafetyChecklistPDF(item) {
      try {
        const checklistId = item.arkMedsChecklist.id
        const response = await this.apiService.post(
          `arkmeds/webhooks/generateElectricalSafetyPdf/${checklistId}`
        )
        if (response.statusCode === 200) {
          const pdfBase64 = response.message

          const pdfUrl = `data:application/pdf;base64,${pdfBase64}`
          const link = document.createElement('a')
          link.href = pdfUrl
          link.setAttribute(
            'download',
            `Checklist_${this.formatDate(new Date().toISOString())}.pdf`
          )
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          this.$toast.success('PDF gerado com sucesso!')
        } else {
          this.$toast.error('Falha ao gerar o PDF.')
        }
      } catch (error) {
        console.error('Erro ao gerar PDF:', error)
        this.$toast.error('Erro ao gerar o PDF.')
      }
    },

    openElectricalSafety(serviceOrder) {
      sessionStorage.clear()
      this.currentServiceOrder = serviceOrder
      sessionStorage.setItem(
        'serviceOrder',
        JSON.stringify(this.currentServiceOrder)
      )
      window.open('/formElectricalSafety', '_blank').focus()
    },

    uploadDocument(serviceOrder) {
      sessionStorage.clear()
      this.currentServiceOrder = serviceOrder

      const order = {
        id: serviceOrder.id,
        number: serviceOrder.number,
      }
      sessionStorage.setItem('serviceOrder', JSON.stringify(order))

      let routeData = this.$router.resolve({ path: '/uploadDocument' })
      window.open(routeData.href, '_blank')
    },

    reset() {
      this.filter = {
        equipment: null,
        identification: null,
        number: null,
      }
      this.getOS()
    },

    editCalibrationData(item) {
      this.openElectricalSafety(item)
    },

    deleteCalibration(item) {
      this.currentCalibration = item
      this.deleteDialog = true
    },
  },

  watch: {
    pageSize() {
      this.getOS()
    },
    page() {
      this.getOS()
    },
  },
}
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: column;
}
.pagination {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
</style>
